import { ChannelConfig, channelConfig } from '@finn-no/adboost/channels';
import React from 'react';
import { PreviewConfig } from '../shared/types/config';
import { Channel } from './types';

type PreviewChannelConfig = {
    shortName: string;
    url: string;
    top: number;
    left: number;
    width: number;
    height: number;
    iframeBackgroundColor?: string;
    backgroundHeight: number;
    backgroundWidth: number;
    backgroundImageUrl: string;
    loadingBackgroundImageUrl?: string;
    phoneUrl: string;
    phoneTop: number;
    phoneLeft: number;
    phoneWidth: number;
    phoneHeight: number;
    phoneBackgroundHeight: number;
    phoneBackgroundWidth: number;
    phoneBackgroundImageUrl: string;
    loadingPhoneBackgroundImageUrl?: string;
    explanation?: JSX.Element;
};

function backgroundImage(shortName: string): string {
    return `https://static.finncdn.no/_c/distribution-studio/2021/${shortName}/desktop.png`;
}
function phoneBackgroundImage(shortName: string): string {
    return `https://static.finncdn.no/_c/distribution-studio/2021/${shortName}/phone.png`;
}

function previewUrl(channel: Channel, config: PreviewConfig) {
    const { finnkode, wwwUrl } = config;
    const pathname = '/distribution-front/external/carousel/preview';
    const search = `?finnkode=${finnkode}&channel=${channel}&variant=control`;
    return `${wwwUrl}${pathname}${search}`;
}

export function previewChannelConfig(channel: Channel, config: PreviewConfig): PreviewChannelConfig & ChannelConfig {
    switch (channel) {
        case 'finn-mail':
            return {
                ...channelConfig(channel),
                shortName: '',
                url: previewUrl(channel, config),
                height: 550,
                width: 654,
                top: 190,
                left: 100,
                backgroundHeight: 812,
                backgroundWidth: 1200,
                backgroundImageUrl: '',
                phoneUrl: '',
                phoneHeight: 320,
                phoneWidth: 357,
                phoneTop: 210,
                phoneLeft: 10,
                phoneBackgroundHeight: 812,
                phoneBackgroundWidth: 375,
                phoneBackgroundImageUrl: '',
                explanation: (
                    <p>
                        Når brukere av FINN får e-post om annonser fra lagrede søk kan de også få annonser fra kampanjer som denne, dersom
                        de er i målgruppen. Disse e-postene sendes altså til andre brukere enn de som får annonsen fra lagret søk.
                        <span className="bullet">En visning betyr at noen har åpnet e-posten med kampanjen.</span>
                        <span className="bullet">Et klikk betyr at de har klikket på kampanjen i e-posten.</span>
                    </p>
                ),
            };
        case 'finn-rec':
            return {
                ...channelConfig(channel),
                shortName: '',
                url: previewUrl(channel, config),
                height: 550,
                width: 654,
                top: 190,
                left: 100,
                backgroundHeight: 812,
                backgroundWidth: 1200,
                backgroundImageUrl: '',
                phoneUrl: '',
                phoneHeight: 320,
                phoneWidth: 357,
                phoneTop: 210,
                phoneLeft: 10,
                phoneBackgroundHeight: 812,
                phoneBackgroundWidth: 375,
                phoneBackgroundImageUrl: '',
                explanation: (
                    <p>
                        På forsiden av FINN.no og markedsforsidene vises en strøm av personlige anbefalinger til alle brukere. Kampanjen får
                        økt synlighet blant disse anbefalingene. FINN.no er en av de mest besøkte nettsidene i Norge, og en svært synlig
                        plassering på forsiden til FINN er en unik mulighet til å treffe alle som er interessert i din FINN-annonse.'
                    </p>
                ),
            };
        case 'vg.no':
            return {
                ...channelConfig(channel),
                shortName: 'vg',
                url: previewUrl(channel, config),
                height: 500,
                width: 738,
                top: 190,
                left: 111,
                backgroundHeight: 812,
                backgroundWidth: 1200,
                backgroundImageUrl: backgroundImage('vg'),
                iframeBackgroundColor: '#FFFFFF',
                phoneUrl: previewUrl(channel, config),
                phoneHeight: 320,
                phoneWidth: 374.797,
                phoneTop: 200,
                phoneLeft: 0,
                phoneBackgroundHeight: 812,
                phoneBackgroundWidth: 375,
                phoneBackgroundImageUrl: phoneBackgroundImage('vg'),
            };
        case 'ap.no':
            return {
                ...channelConfig(channel),
                shortName: 'aftenposten',
                url: previewUrl(channel, config),
                height: 400,
                width: 580,
                top: 270,
                left: 145,
                backgroundHeight: 812,
                backgroundWidth: 1200,
                backgroundImageUrl: backgroundImage('aftenposten'),
                phoneUrl: previewUrl(channel, config),
                phoneHeight: 320,
                phoneWidth: 375,
                phoneTop: 220,
                phoneLeft: -1,
                phoneBackgroundHeight: 812,
                phoneBackgroundWidth: 375,
                phoneBackgroundImageUrl: phoneBackgroundImage('aftenposten'),
            };
        case 'e24':
            return {
                ...channelConfig(channel),
                shortName: 'e24',
                url: previewUrl(channel, config),
                height: 504,
                width: 582,
                top: 200,
                left: 519,
                backgroundHeight: 812,
                backgroundWidth: 1200,
                backgroundImageUrl: backgroundImage('e24'),
                phoneUrl: previewUrl(channel, config),
                phoneHeight: 350,
                phoneWidth: 359,
                phoneTop: 200,
                phoneLeft: 8,
                phoneBackgroundHeight: 812,
                phoneBackgroundWidth: 375,
                phoneBackgroundImageUrl: phoneBackgroundImage('e24'),
            };
        case 'aftenbladet.no':
            return {
                ...channelConfig(channel),
                shortName: 'aftenbladet',
                url: previewUrl(channel, config),
                height: 400,
                width: 580,
                top: 145,
                left: 498,
                backgroundHeight: 812,
                backgroundWidth: 1200,
                backgroundImageUrl: backgroundImage('aftenbladet'),
                phoneUrl: previewUrl(channel, config),
                phoneHeight: 320,
                phoneWidth: 375,
                phoneTop: 210,
                phoneLeft: -1,
                phoneBackgroundHeight: 812,
                phoneBackgroundWidth: 375,
                phoneBackgroundImageUrl: phoneBackgroundImage('aftenbladet'),
            };
        case 'bt.no':
            return {
                ...channelConfig(channel),
                shortName: 'bt',
                url: previewUrl(channel, config),
                height: 400,
                width: 580,
                top: 210,
                left: 129,
                backgroundHeight: 812,
                backgroundWidth: 1200,
                backgroundImageUrl: backgroundImage('bt'),
                phoneUrl: previewUrl(channel, config),
                phoneHeight: 320,
                phoneWidth: 375,
                phoneTop: 199,
                phoneLeft: -1,
                phoneBackgroundHeight: 812,
                phoneBackgroundWidth: 375,
                phoneBackgroundImageUrl: phoneBackgroundImage('bt'),
            };
        case 'fvn.no':
            return {
                ...channelConfig(channel),
                shortName: 'fvn',
                url: previewUrl(channel, config),
                height: 400,
                width: 580,
                top: 185,
                left: 111,
                backgroundHeight: 812,
                backgroundWidth: 1200,
                backgroundImageUrl: backgroundImage('fvn'),
                phoneUrl: previewUrl(channel, config),
                phoneHeight: 320,
                phoneWidth: 375,
                phoneTop: 170,
                phoneLeft: -1,
                phoneBackgroundHeight: 812,
                phoneBackgroundWidth: 375,
                phoneBackgroundImageUrl: phoneBackgroundImage('fvn'),
            };
        case 'tek':
            return {
                ...channelConfig(channel),
                shortName: 'tek',
                url: previewUrl(channel, config),
                height: 454,
                width: 746,
                top: 325,
                left: 38,
                backgroundHeight: 812,
                backgroundWidth: 1200,
                backgroundImageUrl: backgroundImage('tek'),
                phoneUrl: previewUrl(channel, config),
                phoneHeight: 320,
                phoneWidth: 346,
                phoneTop: 185,
                phoneLeft: 13,
                phoneBackgroundHeight: 812,
                phoneBackgroundWidth: 375,
                phoneBackgroundImageUrl: phoneBackgroundImage('tek'),
            };
        case 'facebook':
            return {
                ...channelConfig(channel),
                shortName: 'facebook',
                url: config.facebook?.desktop?.src || '',
                height: 687,
                width: 502,
                top: 72,
                left: 313,
                backgroundHeight: 761,
                backgroundWidth: 1128,
                backgroundImageUrl: backgroundImage('facebook'),
                phoneUrl: config.facebook?.mobile?.src || '',
                /*  phoneHeight: 520,
                phoneWidth: 375,
                phoneTop: 95,
                phoneLeft: -1,
                phoneBackgroundHeight: 812,
                phoneBackgroundWidth: 375, */
                phoneHeight: 690,
                phoneWidth: 320,
                phoneTop: 95,
                phoneLeft: -1,
                phoneBackgroundHeight: 693,
                phoneBackgroundWidth: 320,
                phoneBackgroundImageUrl: phoneBackgroundImage('facebook'),
                loadingPhoneBackgroundImageUrl: 'https://static.finncdn.no/_c/distribution-studio/facebook/phone-loading.png',
            };
        case 'adressa.no':
            return {
                ...channelConfig(channel),
                shortName: 'adressa',
                url: previewUrl(channel, config),
                height: 454,
                width: 575,
                top: 202,
                left: 112,
                backgroundHeight: 812,
                backgroundWidth: 1200,
                backgroundImageUrl: backgroundImage('adressa'),
                phoneUrl: previewUrl(channel, config),
                phoneHeight: 320,
                phoneWidth: 372,
                phoneTop: 164,
                phoneLeft: 0,
                phoneBackgroundHeight: 812,
                phoneBackgroundWidth: 375,
                phoneBackgroundImageUrl: phoneBackgroundImage('adressa'),
            };

        default:
            return {
                ...channelConfig(channel),
                shortName: 'vg',
                url: previewUrl(channel, config),
                height: 550,
                width: 654,
                top: 190,
                left: 100,
                backgroundHeight: 812,
                backgroundWidth: 1200,
                backgroundImageUrl: backgroundImage('vg'),
                phoneUrl: previewUrl(channel, config),
                phoneHeight: 320,
                phoneWidth: 357,
                phoneTop: 210,
                phoneLeft: 10,
                phoneBackgroundHeight: 812,
                phoneBackgroundWidth: 375,
                phoneBackgroundImageUrl: phoneBackgroundImage('vg'),
            };
    }
}
